import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  invitationService,
  ValidateInvitationResponse, 
} from "../services/invitationService";
import { ContentContainer } from "../components/ContentContainer";
import { InvitationDetailSkeleton } from "../components/InvitationDetailSkeleton";
import { InvitationError } from "../components/InvitationError";
import { InvitationDetails } from "../components/InvitationDetails";
import { VisitorInfoForm } from "../components/VisitorInfoForm";
import { InvitationSuccess } from "../components/InvitationSuccess";

const InvitationLanding = () => {
  const [invitationData, setInvitationData] =
    useState<ValidateInvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { inviteToken } = useParams();

  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isNameEditable, setIsNameEditable] = useState(true);

  const [documentType, setDocumentType] = useState<"dni" | "passport" | "other">("dni");
  const [documentNumber, setDocumentNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loadingNameData, setLoadingNameData] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasValidated, setHasValidated] = useState(false);

  const fetchInvitationByToken = async (inviteToken: string) => {
    try {
      const response = await invitationService.getInvitationByToken({ inviteToken });
      if (!response.valid) {
        setError(response.message || "Invitaci?n inv?lida o expirada");
        setLoading(false);
        return;
      }
      setInvitationData(response);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching invitation:", err);
      setError(
        err.message || "Ocurri? un error al obtener la informaci?n de la invitaci?n"
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inviteToken) {
      fetchInvitationByToken(inviteToken);
    } else {
      setError("No invitation token provided");
      setLoading(false);
    }
  }, [inviteToken]);

  const handleContinue = () => {
    setShowForm(true);
  };

  const handleBack = () => {
    setShowForm(false);
    setFormError(null);
    setValidationError(null);
    setHasValidated(false);
    setFirstName("");
    setLastName("");
  };

  const handleValidateDocument = useCallback(async () => {
    if (!documentNumber || documentNumber.length < 5) {
      setValidationError("Por favor, ingresa un n?mero de documento v?lido.");
      return;
    }

    try {
      setLoadingNameData(true);
      setValidationError(null);

      const response = await invitationService.NameByDocument({
        documentNumber,
        documentType,
      });

      setFirstName(response.firstName);
      setLastName(response.lastName);
      setHasValidated(true);

      setIsNameEditable(!response.firstName && !response.lastName);
    } catch (error: any) {
      console.error("Error fetching name by document:", error);
      setFirstName("");
      setLastName("");
      setHasValidated(true);
      setIsNameEditable(true);
    } finally {
      setLoadingNameData(false);
    }
  }, [documentNumber, documentType]);

  const acceptInvitation = async () => {
    if (!inviteToken || !invitationData) {
      setFormError("Informaci?n de invitaci?n no disponible.");
      return;
    }

    if (!documentNumber || !firstName.trim() || !lastName.trim()) {
      setFormError("Por favor complete todos los campos.");
      return;
    }

    try {
      setCreatingInvitation(true);
      setFormError(null);

      await invitationService.postInvitationAcceptance({
        inviteToken,
        aparmentId: invitationData.apartmentId || "",
        buildingId: invitationData.buildingId || "",
        documentNumber,
        documentType,
        firstName,
        lastName,
      });

      setShowSuccess(true);
    } catch (error: any) {
      console.error("Error processing invitation:", error);
      setFormError("Error al procesar la invitaci?n: " + error.message);
    } finally {
      setCreatingInvitation(false);
    }
  };

  if (loading) {
    return (
      <ContentContainer>
        <InvitationDetailSkeleton />
      </ContentContainer>
    );
  }

  if (error) {
    return (
      <ContentContainer>
        <InvitationError message={error} />
      </ContentContainer>
    );
  }

  if (!invitationData) {
    return (
      <ContentContainer>
        <InvitationError message="No se encontraron datos de invitaci?n." />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      {showSuccess ? (
        <InvitationSuccess />
      ) : !showForm ? (
        <InvitationDetails
          invitationData={invitationData}
          onContinue={handleContinue}
        />
      ) : (
        <VisitorInfoForm
          documentType={documentType}
          documentNumber={documentNumber}
          firstName={firstName}
          lastName={lastName}
          loadingNameData={loadingNameData}
          formError={formError}
          onDocumentTypeChange={setDocumentType}
          onDocumentNumberChange={setDocumentNumber}
          onFirstNameChange={setFirstName}
          onLastNameChange={setLastName}
          onBack={handleBack}
          onSubmit={acceptInvitation}
          onValidate={handleValidateDocument}
          validationError={validationError}
          isSubmitting={creatingInvitation}
          documentNumberLength={documentNumber.length}
          hasValidated={hasValidated}
          isNameEditable={isNameEditable}
        />
      )}
    </ContentContainer>
  );
};

export default InvitationLanding;
